/** Player shell: team workspace URL segments (maps to DashboardPage embeddedSection). No `settings` — use /player/settings. */
export const PLAYER_TEAM_SECTIONS = [
  'overview',
  'stats',
  'schedule',
  'results',
  'divisions',
  'reserve-court',
  'team',
];

const STORAGE_KEY = 'player_team_workspace_ctx_v1';
const LAST_ACTIVE_TEAM_KEY = 'player_last_team_workspace_id_v1';

/**
 * Remember which team workspace the player used last (sidebar switcher / deep links).
 * @param {string} teamId
 */
export function setLastActiveTeamWorkspaceId(teamId) {
  if (!teamId) return;
  try {
    sessionStorage.setItem(LAST_ACTIVE_TEAM_KEY, String(teamId));
  } catch {
    /* ignore */
  }
}

/** @returns {string} */
export function getLastActiveTeamWorkspaceId() {
  try {
    return sessionStorage.getItem(LAST_ACTIVE_TEAM_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * @param {string | undefined} section
 * @returns {string}
 */
export function normalizePlayerTeamSection(section) {
  const s = String(section || 'overview')
    .toLowerCase()
    .trim();
  if (s === 'schedule') return 'overview';
  if (PLAYER_TEAM_SECTIONS.includes(s)) return s;
  return 'overview';
}

/**
 * @param {string} teamId
 * @param {string} [section]
 * @param {Record<string, string | number | null | undefined>} [query]
 */
export function buildPlayerTeamWorkspacePath(teamId, section, query = {}) {
  const sec = normalizePlayerTeamSection(section);
  const qs = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v != null && String(v).trim() !== '') qs.set(k, String(v));
  });
  const q = qs.toString();
  return `/player/team/${encodeURIComponent(teamId)}/${encodeURIComponent(sec)}${q ? `?${q}` : ''}`;
}

/**
 * @param {{ teamId: string, tenant_id?: string, season_id?: string, campaign_id?: string }} ctx
 */
export function rememberPlayerTeamWorkspace(ctx) {
  try {
    if (!ctx?.teamId) return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[String(ctx.teamId)] = {
      tenant_id: ctx.tenant_id || '',
      season_id: ctx.season_id || '',
      campaign_id: ctx.campaign_id || '',
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} teamId
 * @returns {{ tenant_id: string, season_id: string, campaign_id: string } | null}
 */
export function readRememberedPlayerTeamWorkspace(teamId) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    const row = map[String(teamId)];
    if (!row || typeof row !== 'object') return null;
    return {
      tenant_id: String(row.tenant_id || ''),
      season_id: String(row.season_id || ''),
      campaign_id: String(row.campaign_id || ''),
    };
  } catch {
    return null;
  }
}

export function isPlayerTeamWorkspacePath(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/player/team/');
}
